"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.minorUnitsSchema = exports.moneySchema = exports.currencyCodeSchema = exports.SUPPORTED_CURRENCIES = void 0;
exports.toMinorUnits = toMinorUnits;
exports.toMajorUnits = toMajorUnits;
exports.isCurrencyCode = isCurrencyCode;
exports.setDisplayLocale = setDisplayLocale;
exports.getDisplayLocale = getDisplayLocale;
exports.formatMoney = formatMoney;
const zod_1 = require("zod");
// Money is an integer count of minor units plus an ISO 4217 code — never a float,
// never a converted value (CLAUDE.md non-negotiable #7, TECH-STACK 3.6).
// Minor units are stored as BIGINT: BDT's minor unit is poisha, so a 25 lakh
// salary is 250,000,000 and department totals pass int4's 2.1 billion ceiling.
/**
 * The ISO 4217 codes the system knows (S31 Q20). Which of them are offered is a setting
 * (ADMIN-15's currency list); this is only what the database and the validators accept.
 */
exports.SUPPORTED_CURRENCIES = [
    'BDT', 'GBP', 'USD', 'EUR', 'INR', 'PKR', 'LKR', 'NPR', 'AED', 'SAR', 'QAR', 'KWD', 'OMR', 'BHD',
    'SGD', 'MYR', 'THB', 'HKD', 'CNY', 'JPY', 'AUD', 'NZD', 'CAD', 'CHF', 'SEK', 'NOK', 'DKK', 'ZAR',
];
exports.currencyCodeSchema = zod_1.z.enum(exports.SUPPORTED_CURRENCIES);
exports.moneySchema = zod_1.z.object({
    amountMinor: zod_1.z.bigint(),
    currency: exports.currencyCodeSchema,
});
/**
 * Money as it crosses the wire: a digit string, never a JSON number.
 *
 * JSON has no bigint, and `JSON.stringify` refuses one — so an amount travels as
 * text in both directions and becomes a bigint at the edge. A JSON number would
 * silently lose precision above 2^53, which for poisha is a salary total nobody
 * would notice was wrong.
 *
 * The 11-digit ceiling is §L26's "max 9 digits, no decimals" expressed in minor
 * units.
 */
exports.minorUnitsSchema = zod_1.z
    .union([
    zod_1.z.string().trim().regex(/^\d{1,11}$/, 'Enter a valid amount.'),
    zod_1.z.number().int().nonnegative().max(99_999_999_999, 'Enter a valid amount.'),
])
    .transform((value) => BigInt(value));
/** ISO 4217's minor unit, from `Intl`: 100 for taka and pounds, 1 for yen, 1000 for dinars. */
function minorUnitsPerMajor(currency) {
    const digits = new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits ?? 2;
    return 10 ** digits;
}
function toMinorUnits(major, currency) {
    return BigInt(Math.round(major * minorUnitsPerMajor(currency)));
}
function toMajorUnits(amountMinor, currency) {
    return Number(amountMinor) / minorUnitsPerMajor(currency);
}
function isCurrencyCode(value) {
    return exports.SUPPORTED_CURRENCIES.some((code) => code === value);
}
/**
 * S31 (ADMIN-15, O3): the number grouping the organisation reads — `en-GB` (100,000) by default,
 * `en-IN` (1,00,000) when chosen. Set once from the settings at sign-in; a caller may still pass
 * its own locale.
 */
let displayLocale = 'en-GB';
function setDisplayLocale(locale) {
    displayLocale = locale;
}
function getDisplayLocale() {
    return displayLocale;
}
/**
 * 06-qol §11: currency ALWAYS shows its symbol. `narrowSymbol` is what turns
 * `BDT 65,000` into `৳65,000` and `US$` into `$` — the default display spells the
 * ISO code out, which reads as a database field rather than a price.
 */
function formatMoney(money, options = {}) {
    return new Intl.NumberFormat(options.locale ?? displayLocale, {
        style: 'currency',
        currency: money.currency,
        currencyDisplay: 'narrowSymbol',
        maximumFractionDigits: options.maximumFractionDigits ?? 0,
    }).format(toMajorUnits(money.amountMinor, money.currency));
}
//# sourceMappingURL=money.js.map